import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FaTimes, FaArrowRight } from 'react-icons/fa';
import './TutorialOverlay.css';

interface TutorialOverlayProps {
  isActive: boolean;
  step: number;
  targetElement?: string;
  message: string;
  buttonText?: string;
  onNext: () => void;
  onSkip: () => void;
  showSkip?: boolean;
  waitForAction?: boolean;
}

export const TutorialOverlay: React.FC<TutorialOverlayProps> = ({
  isActive,
  step,
  targetElement,
  message,
  buttonText = "Siguiente",
  onNext,
  onSkip,
  showSkip = true,
  waitForAction = false
}) => {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const location = useLocation();
  
  // Función para calcular la posición óptima del mensaje
  const getMessagePosition = (rect: DOMRect) => {
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    const messageWidth = 400; // max-width del mensaje
    const messageHeight = 200; // altura aproximada del mensaje
    
    // Si el elemento está en la parte superior, poner el mensaje abajo con más separación
    if (rect.top < windowHeight / 2) {
      return {
        top: rect.bottom + 40, // Aumentado de 20 a 40 para más separación
        left: Math.max(20, Math.min(windowWidth - messageWidth - 20, rect.left + rect.width / 2 - messageWidth / 2)),
        transform: 'none'
      };
    } else {
      // Si está en la parte inferior, poner el mensaje arriba
      return {
        top: rect.top - messageHeight - 40, // También aumentado aquí para consistencia
        left: Math.max(20, Math.min(windowWidth - messageWidth - 20, rect.left + rect.width / 2 - messageWidth / 2)),
        transform: 'none'
      };
    }
  };

  useEffect(() => {
    // Añadir clase al body para indicar que estamos en el paso 9
    if (step === 9) {
      document.body.classList.add('tutorial-step-9');
    } else {
      document.body.classList.remove('tutorial-step-9');
    }

    if (targetElement) {
      let cleanup: (() => void) | null = null;

      // Función para buscar y configurar el elemento
      const setupElement = () => {
        const element = document.querySelector(`[data-tutorial-target="${targetElement}"]`) as HTMLElement;
        if (targetElement === 'delete-ship-button-aurora-cl') {
          console.log('TUTORIAL: Looking for', targetElement, 'found element:', !!element);
          if (element) {
            const shipCard = element.closest('.ship-card');
            const shipName = shipCard?.querySelector('h2')?.textContent;
            console.log('TUTORIAL: Element belongs to ship:', shipName);
          }
        }
        if (element) {
          const rect = element.getBoundingClientRect();
          setTargetRect(rect);

          // Asegurar que el elemento sea clicable
          // Solo cambiar position si no es absolute o fixed
          const currentPosition = window.getComputedStyle(element).position;
          const shouldChangePosition = currentPosition !== 'absolute' && currentPosition !== 'fixed';
          if (shouldChangePosition) {
            element.style.position = 'relative';
          }
          element.style.zIndex = '10001';
          element.style.pointerEvents = 'auto';

          // Añadir listener para detectar interacción con elemento destacado
          const handleClick = () => {
            if (waitForAction) {
              onNext();
            }
          };

          // Solo añadir click listener, el input será manejado por la integración directa
          element.addEventListener('click', handleClick);
          return () => {
            element.removeEventListener('click', handleClick);
            // Restaurar estilos originales solo si los cambiamos
            if (shouldChangePosition) {
              element.style.position = '';
            }
            element.style.zIndex = '';
            element.style.pointerEvents = '';
          };
        }
        return null;
      };

      // Intentar configurar inmediatamente
      cleanup = setupElement();
      
      // Si no se encontró, usar MutationObserver para detectar cuando aparezca
      if (!cleanup) {
        const observer = new MutationObserver(() => {
          if (!cleanup) {
            cleanup = setupElement();
            if (cleanup) {
              observer.disconnect();
            }
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true
        });

        return () => {
          observer.disconnect();
          if (cleanup) cleanup();
        };
      }

      return cleanup;
    }

    // Cleanup: quitar la clase del body al desmontar
    return () => {
      document.body.classList.remove('tutorial-step-9');
    };
  }, [targetElement, waitForAction, onNext, step]);

  // Actualizar posición del elemento cuando cambia el tamaño de ventana
  useEffect(() => {
    const updateRect = () => {
      if (targetElement) {
        const element = document.querySelector(`[data-tutorial-target="${targetElement}"]`);
        if (element) {
          const rect = element.getBoundingClientRect();
          setTargetRect(rect);
        }
      }
    };

    window.addEventListener('resize', updateRect);
    return () => window.removeEventListener('resize', updateRect);
  }, [targetElement]);

  // Recalcular posición cuando cambie la ruta (para el paso 2 del tutorial)
  useEffect(() => {
    if (targetElement === 'search-bar' && isActive) {
      // Pequeño delay para permitir que el DOM se actualice después del cambio de ruta
      const timer = setTimeout(() => {
        const element = document.querySelector(`[data-tutorial-target="${targetElement}"]`);
        if (element) {
          const rect = element.getBoundingClientRect();
          setTargetRect(rect);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, targetElement, isActive]);

  if (!isActive) return null;

  return (
    <div className="tutorial-overlay">
      {/* Fondo oscurecido con agujeros usando múltiples divs */}
      {targetRect ? (
        <>
          {/* Top */}
          <div 
            className="tutorial-backdrop-section"
            style={{
              top: 0,
              left: 0,
              right: 0,
              height: targetRect.top - 2
            }}
          />
          {/* Left */}
          <div 
            className="tutorial-backdrop-section"
            style={{
              top: targetRect.top - 2,
              left: 0,
              width: targetRect.left - 4,
              height: targetRect.height + 4
            }}
          />
          {/* Right */}
          <div 
            className="tutorial-backdrop-section"
            style={{
              top: targetRect.top - 2,
              left: targetRect.right + 4,
              right: 0,
              height: targetRect.height + 4
            }}
          />
          {/* Bottom */}
          <div 
            className="tutorial-backdrop-section"
            style={{
              top: targetRect.bottom + 2,
              left: 0,
              right: 0,
              bottom: 0
            }}
          />
          {/* Borde destacado para elemento */}
          <div 
            className="tutorial-highlight"
            style={{
              left: targetRect.left - 4,
              top: targetRect.top - 2,
              width: targetRect.width + 8,
              height: targetRect.height + 4
            }}
          />
        </>
      ) : (
        <div className="tutorial-backdrop" />
      )}
      
      {/* Mensaje del tutorial */}
      <div 
        className="tutorial-message"
        style={targetRect ? getMessagePosition(targetRect) : {}}
      >
        <div className="tutorial-content">
          {showSkip && (
            <button 
              className="tutorial-skip"
              onClick={onSkip}
              title="Saltar tutorial"
            >
              <FaTimes />
            </button>
          )}
          
          <div className="tutorial-step-indicator">
            Paso {step} de 10
          </div>
          
          <p className="tutorial-text">{message}</p>
          
          {!waitForAction && (
            <button 
              className="tutorial-next-btn"
              onClick={onNext}
            >
              {buttonText} <FaArrowRight />
            </button>
          )}

          {waitForAction && (
            <p className="tutorial-wait-text">
              👆 Haz clic en el elemento destacado para continuar
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
